/**
 * Per-user read/dismissed state for notification documents.
 *
 * A notification document is shared by every recipient (all admins, every
 * manager at a location, ...). Who has read or cleared it is therefore tracked
 * per user in `status.readBy` / `status.dismissedBy` (lists of auth uids), not
 * in a document-wide flag: one recipient acknowledging a notification must
 * never mark it read, or hide it, for anyone else.
 */

export type NotificationStatusField = 'readBy' | 'dismissedBy';

const uidList = (status: unknown, field: NotificationStatusField): string[] => {
  if (!status || typeof status !== 'object') return [];
  const value = (status as Record<string, unknown>)[field];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string');
};

const includesUser = (status: unknown, field: NotificationStatusField, uid: string | null | undefined): boolean =>
  Boolean(uid) && uidList(status, field).includes(uid as string);

/** True when the given user has read/acknowledged the notification. */
export const hasUserRead = (status: unknown, uid: string | null | undefined): boolean =>
  includesUser(status, 'readBy', uid);

/** True when the given user has cleared the notification from their feed. */
export const hasUserDismissed = (status: unknown, uid: string | null | undefined): boolean =>
  includesUser(status, 'dismissedBy', uid);
